import type { Organization } from "@/stores/organization/organization-store";
import { getApiErrorRecord, getErrorMessageOrFallback } from "@/utils/errors";

const SPONSORSHIP_CAPS = {
  free: 0,
  basic: 15,
  standard: 30,
  pro: 50,
  enterprise: 999999,
} as const;

export interface SponsorshipEligibility {
  eligible: boolean;
  reason?: string;
}

export function getSponsorshipCap(planType: string): number {
  switch (planType) {
    case "free":
      return SPONSORSHIP_CAPS.free;
    case "basic":
      return SPONSORSHIP_CAPS.basic;
    case "standard":
      return SPONSORSHIP_CAPS.standard;
    case "pro":
      return SPONSORSHIP_CAPS.pro;
    case "enterprise":
      return SPONSORSHIP_CAPS.enterprise;
    default:
      return 0;
  }
}

export function getSponsorshipEligibility(
  organization: Organization,
): SponsorshipEligibility {
  const billingPlan = organization.billingPlan;

  if (billingPlan.type === "free") {
    return {
      eligible: false,
      reason: "Free plan",
    };
  }

  if (billingPlan.status === "trialing") {
    return {
      eligible: false,
      reason: "Trial ineligible",
    };
  }

  if (billingPlan.status === "past_due") {
    return {
      eligible: false,
      reason: "Past due",
    };
  }

  if (billingPlan.status === "canceled" || billingPlan.status === "suspended") {
    return {
      eligible: false,
      reason: "No sponsorship capability",
    };
  }

  if (getSponsorshipCap(billingPlan.type) <= 0) {
    return {
      eligible: false,
      reason: "No sponsorship capability",
    };
  }

  return { eligible: true };
}

function getErrorCode(error: unknown): string | undefined {
  const apiError = getApiErrorRecord(error);
  return typeof apiError?.code === "string" ? apiError.code : undefined;
}

export function getSponsorshipErrorMessage(
  error: unknown,
  fallback: string,
): string {
  switch (getErrorCode(error)) {
    case "NOT_PAID_PLAN":
      return "Sponsorships require an active paid plan for the selected organization.";
    case "SUB_ACCOUNT_LIMIT_REACHED":
      return "This organization has already used all of its sponsored sub-account slots.";
    case "EMAIL_ALREADY_SPONSORED_BY_YOU":
      return "That email already has an active or pending sponsorship from this organization.";
    case "INVITE_EMAIL_MISMATCH":
      return "This invite was sent to a different verified email address.";
    case "PERSONAL_WORKSPACE_DORMANT":
      return "Your personal workspace is dormant. Upgrade or reactivate it before continuing.";
    case "NO_MATCHING_VAULT_ASSIGNMENT":
      return "This sponsorship needs a matching AI budget vault assignment before usage can continue.";
    default:
      return getErrorMessageOrFallback(error, fallback);
  }
}
